import type {
  Bonus,
  ComparisonRow,
  Faq,
  PainPoint,
  SolutionPillar,
  Testimonial,
  ValueRow,
  Volume,
} from '../types/content'

export const PRODUCT = {
  brand: 'REDONDOLAND™',
  name: 'PAIN RESET',
  price: 'USD 27',
  email: 'redondoland1@gmail.com',
  site: 'redondoland.impultienda.ar',
  packImage:
    'https://cdn.magicpatterns.com/patterns/generated-images/b8a05453-0b56-454e-898c-bb2e9c504dcb.jpg',
}

export const HERO_BADGE_LINE =
  '360+ pages · 6 volumes · 4 free bonuses · 2-min download · 100% PDF'

export const TRUST_STRIP = [
  { emoji: '⭐', label: '4.9/5' },
  { emoji: '👥', label: '+1,200 people relieved' },
  { emoji: '📚', label: '6 Volumes' },
  { emoji: '🎁', label: '4 Bonuses' },
  { emoji: '🛡️', label: '7-day guarantee' },
]

export const TOP_TESTIMONIALS: Testimonial[] = [
  {
    quote:
      '3 years of neck pain. In the first week I felt a difference that no massage therapist had ever given me.',
    name: 'Valentina R.',
    location: 'Córdoba',
    role: 'UX Designer',
  },
  {
    quote:
      'The pain map made me understand why my lower back pain was coming from my psoas. I had never connected those dots.',
    name: 'Luciana M.',
    location: 'Buenos Aires',
    role: 'Account Executive',
  },
  {
    quote:
      'Volume 4 changed my squat completely. No more knee pain and I finally feel my glutes working.',
    name: 'Martina S.',
    location: 'Rosario',
    role: 'Personal Trainer',
  },
]

export const BOTTOM_TESTIMONIALS: Testimonial[] = [
  {
    quote:
      'The Desk Kit is on my phone. Between meetings, 5 minutes and the neck stops squeezing. Essential.',
    name: 'Carolina V.',
    location: 'Paraná',
    role: 'Project Manager',
  },
  {
    quote:
      'My husband learned Bonus 4 in 15 minutes. The lower back release he does is incredible. Every Sunday now.',
    name: 'Sofía T.',
    location: 'Mendoza',
    role: 'Architect',
  },
  {
    quote:
      '3 years of plantar fasciitis. Vol. 6 was the only thing that actually worked. I wish I had found this sooner.',
    name: 'Pablo A.',
    location: 'Neuquén',
    role: 'Runner',
  },
  {
    quote:
      'My knee pain was coming from my hip the whole time. Volume 5 changed how I train completely.',
    name: 'Cecilia R.',
    location: 'Rosario',
    role: 'Coach',
  },
]

export const PAIN_POINTS: PainPoint[] = [
  {
    emoji: '🤷',
    title: 'You treat where it hurts — not where it starts',
    body: 'Every massage and painkiller treats the echo. The source stays untouched.',
  },
  {
    emoji: '😤',
    title: 'Massage sessions help but don’t last',
    body: 'A week later the body is back to square one.',
  },
  {
    emoji: '💸',
    title: 'Your gadgets are collecting dust',
    body: 'Without knowing WHERE the tension starts, no device helps.',
  },
  {
    emoji: '🖥️',
    title: '8 hours at a screen undoing your work',
    body: 'The problem isn’t effort — it’s not having the right map.',
  },
  {
    emoji: '🔄',
    title: 'Same cycle, every single week',
    body: 'Monday okay. Thursday locked. Sunday trying to fix it again.',
  },
  {
    emoji: '❓',
    title: 'Nobody explained where it begins',
    body: 'Your back isn’t weak — your chest is tight. The source is never where the pain is.',
  },
]

export const SOLUTION_PILLARS: SolutionPillar[] = [
  {
    emoji: '🗺️',
    title: 'The full body alignment map',
    body: 'Every zone mapped clinically. You work the origin, not the symptom.',
  },
  {
    emoji: '📋',
    title: '60+ release and corrective techniques',
    body: 'Which muscle, how to position, how much pressure, what to feel.',
  },
  {
    emoji: '🗓️',
    title: 'Ready-made routines',
    body: 'Morning, night, desk, post-workout. Pick one and follow it.',
  },
  {
    emoji: '🎯',
    title: 'The 21-Day Alignment System',
    body: 'Identify, release, make it a habit. Trackers included.',
  },
]

export const VOLUMES: Volume[] = [
  {
    number: 'VOL. 1',
    emoji: '📘',
    title: 'THE NECK & JAW RESET',
    zones: 'Neck · Cervicals · Jaw · Tech Neck',
    body: 'The deep muscles at the base of the skull and jaw are the real source of most neck pain and headaches.',
    bullets: [
      'Suboccipital and SCM release',
      'Jaw decompression protocol',
      'Tech neck corrective sequence',
    ],
  },
  {
    number: 'VOL. 2',
    emoji: '📗',
    title: 'THE SHOULDER & UPPER BACK CODE',
    zones: 'Shoulders · Scapulas · Posture',
    body: 'Your back isn’t weak. Your pectoralis minor is pulling your scapulas forward.',
    bullets: [
      'Pectoralis minor release',
      'Scapular repositioning',
      'Lower trapezius activation',
    ],
  },
  {
    number: 'VOL. 3',
    emoji: '📙',
    title: 'THE LUMBAR & CORE SHIELD',
    zones: 'Lower Back · Core · Pelvis',
    body: 'A short psoas and a sleeping deep core are behind most chronic lower back pain.',
    bullets: ['Psoas and QL release', 'Deep core activation', 'Pelvic stability sequence'],
  },
  {
    number: 'VOL. 4',
    emoji: '📕',
    title: 'THE GLUTE & PELVIC ENGINE',
    zones: 'Glutes · Hip · Pelvic Floor',
    body: 'Your glutes aren’t weak — they’re switched off. Your lower back is doing their job.',
    bullets: ['Glute activation sequence', 'Squat and lunge correction', 'Hip impingement protocol'],
  },
  {
    number: 'VOL. 5',
    emoji: '📓',
    title: 'THE HIP & KNEE ALIGNMENT CODE',
    zones: 'Hip · Knee · IT Band',
    body: 'Hip and knee are one chain. Fix the hip and the knee pain disappears.',
    bullets: ['TFL and IT band release', 'VMO activation', 'Dynamic valgus correction'],
  },
  {
    number: 'VOL. 6',
    emoji: '📔',
    title: 'THE ARM, WRIST & FOOT RESET',
    zones: 'Arms · Wrists · Feet',
    body: 'Tennis elbow, carpal tunnel and plantar fasciitis: the most ignored link in the chain.',
    bullets: ['Plantar fascia release', 'Foot arch activation', 'Wrist and forearm reset'],
  },
]

export const VOLUME_INCLUDES = [
  '60 pages of clinical content',
  'Release + corrective exercises',
  'Maintenance protocol + tracker',
]

export const FOR_YOU = [
  'Your neck and shoulders are loaded by the end of every day',
  'Your lower back complains after hours of sitting or standing',
  'Massages help for 48 hours then back to zero',
  'Your gadgets are in a drawer collecting dust',
  'Your glutes don’t respond and your lower back takes over everything',
  'You want a simple daily routine — not another impossible commitment',
  'You’ve tried everything and the pain always comes back',
]

export const BONUSES: Bonus[] = [
  {
    label: 'BONUS 01',
    title: 'THE PAIN SOURCE FINDER MAP',
    body: 'Every pain zone connected to its real origin and the volume that fixes it.',
    value: 'USD 29',
    image: 'https://cdn.magicpatterns.com/uploads/bpCvZUMSDrxyu2Jz1Ukzs1/IMAGEN_BONO_1.webp',
  },
  {
    label: 'BONUS 02',
    title: 'THE 10-MINUTE DAILY BODY RESET',
    body: 'Morning and night versions. 10 minutes to stay aligned every day.',
    value: 'USD 19',
    image: 'https://cdn.magicpatterns.com/uploads/woWTNhKx15GRpqvt1cFjEN/IMAGEN_BONO_2.webp',
  },
  {
    label: 'BONUS 03',
    title: 'THE DESK PAIN EMERGENCY KIT',
    body: '5 minutes at your desk: neck, shoulders, lower back and wrists.',
    value: 'USD 19',
    image: 'https://cdn.magicpatterns.com/uploads/9fBXh1s2Npwnda3spWV2yT/IMAGEN_BONO_3.webp',
  },
  {
    label: 'BONUS 04',
    title: 'PARTNER MASSAGE MASTERY',
    body: '14 assisted techniques. Anyone learns it in 15 minutes. No equipment.',
    value: 'USD 29',
    image: 'https://cdn.magicpatterns.com/uploads/m6fWAMQkZcFxBGKTowZpiW/IMAGEN_BONO_4.png',
  },
]

export const VALUE_ROWS: ValueRow[] = [
  { item: 'Pain Reset — 6 Volumes (360+ pages)', value: 'USD 97', free: false },
  { item: 'Pain Source Finder Map', value: 'FREE', free: true },
  { item: '10-Minute Daily Body Reset', value: 'FREE', free: true },
  { item: 'Desk Pain Emergency Kit', value: 'FREE', free: true },
  { item: 'Partner Massage Mastery', value: 'FREE', free: true },
]

export const COMPARISON_ROWS: ComparisonRow[] = [
  {
    situation: 'Neck pain every day',
    without: 'Massage every 2 weeks. Temporary.',
    with: 'Source identified. Fixed.',
  },
  {
    situation: 'Back locked in the morning',
    without: 'Anti-inflammatory and wait',
    with: 'Psoas released. No pills.',
  },
  {
    situation: 'Glutes that don’t respond',
    without: 'More squats. More knee pain.',
    with: 'Activation sequence.',
  },
  {
    situation: 'Pain during work',
    without: 'Endure until evening',
    with: '5-minute desk reset.',
  },
  {
    situation: 'Next year',
    without: 'Same cycle. Same pain.',
    with: 'Aligned. Permanent habit.',
  },
]

export const FAQS: Faq[] = [
  {
    question: 'When do I receive it?',
    answer:
      'Immediately. Download link in less than 2 minutes. Works on any device.',
  },
  {
    question: 'Do I need experience or equipment?',
    answer: 'No. A floor, a chair, and optionally a tennis ball. That’s it.',
  },
  {
    question: 'How much time per day?',
    answer: '10 minutes minimum. The desk kit works in 5.',
  },
  {
    question: 'What age does it work for?',
    answer:
      '25 to 65. Desk workers, active people, runners, postpartum — there is a volume for each situation.',
  },
  {
    question: 'What makes it different from YouTube?',
    answer:
      'Every volume finds the real anatomical source — not just the symptom — and applies a release, activation and maintenance sequence. Diagnosis first. Correction second.',
  },
  {
    question: 'Does it replace a doctor?',
    answer:
      'No. This is educational and wellness content. Bonus 1 includes a guide for identifying when professional consultation is needed.',
  },
  {
    question: 'What if I’m not convinced?',
    answer:
      'Write to redondoland1@gmail.com within 7 days. Full refund. No questions.',
  },
]
